<template>
  <div class="terminal-file-view">
    <!-- 顶部 Header -->
    <div class="view-header">
      <div class="header-bg"></div>
      <div class="header-content">
        <div class="header-left">
          <button class="back-btn" @click="router.back()">
            <el-icon><ArrowLeft /></el-icon>
            <span>返回</span>
          </button>
          <div class="header-icon">
            <el-icon><Files /></el-icon>
          </div>
          <div>
            <h1 class="header-title">终端文件管理</h1>
            <p class="header-subtitle">
              船舶ID：<span class="ship-id-tag">{{ shipId }}</span>
              <span class="dot">·</span>
              协议文件查看与同步
            </p>
          </div>
        </div>
        <el-button
          type="primary"
          :loading="syncing"
          class="sync-btn"
          @click="handleSync"
        >
          <el-icon><Refresh /></el-icon>
          <span>{{ syncing ? "同步中..." : "同步终端文件" }}</span>
        </el-button>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-row">
      <div class="stat-card stat-blue">
        <div class="stat-icon">
          <el-icon><Document /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-label">终端文件数</div>
          <div class="stat-value">{{ files.length }}</div>
        </div>
      </div>
      <div class="stat-card stat-green">
        <div class="stat-icon">
          <el-icon><Connection /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-label">翻译记录数</div>
          <div class="stat-value">{{ translations.length }}</div>
        </div>
      </div>
      <div
        class="stat-card stat-red"
        :class="{ 'stat-alert': missingDevices.length > 0 }"
      >
        <div class="stat-icon">
          <el-icon><WarningFilled /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-label">缺失文件设备</div>
          <div class="stat-value">{{ missingDevices.length }}</div>
        </div>
      </div>
      <div class="stat-card stat-purple">
        <div class="stat-icon">
          <el-icon><Clock /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-label">最近同步</div>
          <div class="stat-value stat-time">{{ lastSyncTime || "未同步" }}</div>
        </div>
      </div>
    </div>

    <!-- 主体内容 -->
    <div class="main-content">
      <el-tabs v-model="activeTab" class="main-tabs">
        <!-- 终端协议文件 Tab -->
        <el-tab-pane name="files">
          <template #label>
            终端协议文件
            <el-badge
              v-if="missingDevices.length > 0"
              :value="missingDevices.length"
              type="danger"
              class="tab-badge"
            />
          </template>

          <!-- 对账告警：库有板无（提示可删除） -->
          <el-alert
            v-if="missingDevices.length > 0"
            type="warning"
            show-icon
            :closable="false"
            class="reconcile-alert"
          >
            <template #title>
              对账发现
              {{ missingDevices.length }}
              个后端设备在终端无对应协议文件（设备实际不可用），
              可到<b>用户端-设备管理</b>中删除
            </template>
            <div class="missing-list">
              <div
                v-for="d in missingDevices"
                :key="d.deviceId"
                class="missing-item"
              >
                <span class="missing-name">{{ d.devName }}</span>
                <span class="missing-en">{{
                  d.protName || d.enName || "（未推送协议）"
                }}</span>
                <el-tag
                  v-if="d.terminalMissing"
                  size="small"
                  type="info"
                  effect="plain"
                >
                  已置灰
                </el-tag>
              </div>
            </div>
          </el-alert>

          <div v-if="files.length === 0" class="empty-state">
            <el-empty description="暂无终端文件，请点击右上角同步">
              <el-button type="primary" @click="handleSync">立即同步</el-button>
            </el-empty>
          </div>
          <div v-else class="file-grid">
            <div v-for="(file, idx) in files" :key="idx" class="file-card">
              <div class="file-card-icon">
                <el-icon><Document /></el-icon>
              </div>
              <div class="file-card-content">
                <div class="file-name-cn">
                  {{ file.devName || "未知设备" }}
                </div>
                <div class="file-name-en" :title="displayFileName(file)">
                  <el-icon class="file-name-icon"><FolderOpened /></el-icon>
                  {{ displayFileName(file) }}
                </div>
                <div class="file-card-actions">
                  <el-tag
                    v-if="file.matched"
                    size="small"
                    type="success"
                    effect="light"
                    class="file-tag"
                  >
                    已匹配
                  </el-tag>
                  <template v-else>
                    <el-tag
                      size="small"
                      type="warning"
                      effect="light"
                      class="file-tag"
                    >
                      未匹配
                    </el-tag>
                    <el-button
                      size="small"
                      type="primary"
                      text
                      @click="openAddDialog(file)"
                    >
                      加入翻译表
                    </el-button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- 翻译对应关系 Tab -->
        <el-tab-pane label="翻译对应关系" name="translations">
          <div class="trans-header">
            <el-input
              v-model="translationSearch"
              placeholder="搜索中文名或英文名"
              clearable
              class="search-input"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button class="refresh-btn" @click="loadTranslations">
              <el-icon><Refresh /></el-icon>
              <span>刷新</span>
            </el-button>
          </div>
          <div v-if="filteredTranslations.length === 0" class="empty-state">
            <el-empty
              :description="
                translationSearch ? '没有匹配的翻译记录' : '暂无翻译记录'
              "
            />
          </div>
          <el-table
            v-else
            :data="filteredTranslations"
            border
            stripe
            class="translation-table"
          >
            <el-table-column type="index" label="#" width="60" align="center" />
            <el-table-column label="中文名" min-width="200">
              <template #default="{ row }">
                <div class="cell-cn">
                  <el-icon><ChatLineRound /></el-icon>
                  <span>{{ row.devName }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="英文名" min-width="240">
              <template #default="{ row }">
                <div class="cell-en">
                  <el-icon><Link /></el-icon>
                  <span>{{ row.enName }}</span>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 加入翻译表对话框 -->
    <el-dialog
      v-model="addDialogVisible"
      title="加入翻译表"
      width="460px"
      :close-on-click-modal="false"
    >
      <el-form label-width="90px">
        <el-form-item label="终端文件名">
          <span class="dialog-file-name">{{ addForm.fileName }}</span>
        </el-form-item>
        <el-form-item label="中文名">
          <el-input v-model="addForm.devName" placeholder="设备中文名称" />
        </el-form-item>
        <el-form-item label="英文名">
          <el-input v-model="addForm.enName" placeholder="英文名称（去后缀）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="adding"
          @click="handleAddTranslation"
        >
          确认加入
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import {
  ArrowLeft,
  ChatLineRound,
  Clock,
  Connection,
  Document,
  Files,
  FolderOpened,
  Link,
  Refresh,
  Search,
  WarningFilled,
} from "@element-plus/icons-vue";
import {
  syncProtocolFilesApi,
  listTranslationsApi,
  listProtocolFilesApi,
  addTranslationApi,
  type ProtocolFile,
  type MissingDevice,
} from "@/api/svc005Api";

const route = useRoute();
const router = useRouter();
const shipId = ref((route.query.shipId as string) || "");
const activeTab = ref("files");
const syncing = ref(false);
/** 终端协议文件列表（同步结果中的 files） */
const files = ref<ProtocolFile[]>([]);
/** 库有板无的设备清单（只读对账结果：提示可删除，不自动删） */
const missingDevices = ref<MissingDevice[]>([]);
const translations = ref<ProtocolFile[]>([]);
const translationSearch = ref("");
const lastSyncTime = ref("");
const addDialogVisible = ref(false);
const adding = ref(false);
const addForm = ref({ fileName: "", devName: "", enName: "" });

/** 搜索过滤后的翻译记录 */
const filteredTranslations = computed(() => {
  const kw = translationSearch.value.trim().toLowerCase();
  if (!kw) return translations.value;
  return translations.value.filter(
    (t) =>
      (t.devName || "").toLowerCase().includes(kw) ||
      (t.enName || "").toLowerCase().includes(kw)
  );
});

/** 格式化时间戳为 yyyy-MM-dd HH:mm:ss */
const formatTime = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`;
};

/**
 * 展示板子文件名：统一带 .json 后缀（一眼可辨是终端上的协议文件，区别于上方中文设备名）。
 * 同步接口返回 fileName 自带后缀；缓存查询回退 enName 需补全；中文旧文件名保持原样。
 */
const displayFileName = (file: ProtocolFile): string => {
  const name = file.fileName || file.enName || "";
  if (!name) return "";
  return name.includes(".") ? name : `${name}.json`;
};

/** [5,7] 同步终端协议文件列表 + 库有板无对账 */
const handleSync = async () => {
  if (!shipId.value) {
    ElMessage.warning("缺少船舶ID");
    return;
  }
  syncing.value = true;
  try {
    const result = await syncProtocolFilesApi(shipId.value);
    if (result) {
      files.value = result.files || [];
      missingDevices.value = result.missingDevices || [];
      lastSyncTime.value = formatTime(new Date());
      const missing = missingDevices.value.length;
      ElMessage.success(
        missing > 0
          ? `同步成功，共 ${files.value.length} 个文件；对账发现 ${missing} 个设备终端缺文件，请查看告警`
          : `同步成功，共 ${files.value.length} 个文件，对账无差异`
      );
    }
  } finally {
    syncing.value = false;
  }
};

/** 查询终端协议文件缓存（附带对账结果） */
const loadProtocolFiles = async () => {
  if (!shipId.value) return;
  const result = await listProtocolFilesApi(shipId.value);
  if (result) {
    files.value = result.files || [];
    missingDevices.value = result.missingDevices || [];
    // 取最新一条的 syncTime 显示（列表按 syncTime 降序）
    lastSyncTime.value = files.value[0]?.syncTime || "";
  }
};

/** 打开"加入翻译表"对话框，预填文件名信息 */
const openAddDialog = (file: ProtocolFile) => {
  addForm.value = {
    fileName: file.fileName || file.enName,
    devName: file.devName || "",
    enName: file.enName,
  };
  addDialogVisible.value = true;
};

/** 确认加入翻译表 */
const handleAddTranslation = async () => {
  if (!addForm.value.devName || !addForm.value.enName) {
    ElMessage.warning("中文名和英文名都不能为空");
    return;
  }
  adding.value = true;
  try {
    const ok = await addTranslationApi(
      shipId.value,
      addForm.value.enName,
      addForm.value.devName
    );
    if (ok) {
      ElMessage.success("已加入翻译表");
      addDialogVisible.value = false;
      await loadProtocolFiles();
    }
  } finally {
    adding.value = false;
  }
};

/** 查询所有翻译对应关系 */
const loadTranslations = async () => {
  const result = await listTranslationsApi();
  if (result) translations.value = result;
};

onMounted(() => {
  loadTranslations();
  if (shipId.value) loadProtocolFiles();
});
</script>

<style scoped>
.terminal-file-view {
  min-height: 100vh;
  background: linear-gradient(180deg, #f5f7fa 0%, #e9ecef 100%);
}

/* ===== Header ===== */
.view-header {
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 36px 48px;
  color: #fff;
  overflow: hidden;
}

.header-bg {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
      circle at 20% 30%,
      rgba(255, 255, 255, 0.15) 0%,
      transparent 40%
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(255, 255, 255, 0.12) 0%,
      transparent 40%
    );
}

.header-content {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 22px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.28);
  transform: translateX(-2px);
}

.header-icon {
  width: 68px;
  height: 68px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.header-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.header-subtitle {
  margin: 8px 0 0;
  font-size: 14px;
  opacity: 0.92;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ship-id-tag {
  display: inline-block;
  padding: 3px 12px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  font-family: "Courier New", monospace;
  font-weight: 600;
  font-size: 13px;
}

.dot {
  opacity: 0.7;
}

.sync-btn {
  height: 46px;
  padding: 0 26px;
  font-size: 15px;
  font-weight: 500;
  background: #fff !important;
  color: #667eea !important;
  border: none !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  transition: all 0.25s ease;
}

.sync-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
}

/* ===== Stats ===== */
.stat-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  padding: 24px 48px 0;
  margin-top: -18px;
  position: relative;
  z-index: 2;
}

.stat-card {
  background: #fff;
  border-radius: 14px;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  gap: 18px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #fff;
  flex-shrink: 0;
}

.stat-blue .stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-green .stat-icon {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

.stat-purple .stat-icon {
  background: linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%);
}

.stat-red .stat-icon {
  background: linear-gradient(135deg, #f5576c 0%, #f093fb 100%);
}

/* 有缺失设备时红色卡片呼吸提醒 */
.stat-red.stat-alert {
  animation: stat-pulse 2s ease-in-out infinite;
}

@keyframes stat-pulse {
  0%,
  100% {
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
  }
  50% {
    box-shadow: 0 6px 24px rgba(245, 87, 108, 0.35);
  }
}

.stat-label {
  font-size: 13px;
  color: #94a3b8;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 26px;
  font-weight: 600;
  color: #1e293b;
  font-family: "Helvetica Neue", Arial, sans-serif;
  line-height: 1.2;
}

/* 时间值缩小避免撑爆卡片 */
.stat-value.stat-time {
  font-size: 16px;
  line-height: 26px;
}

/* ===== 对账告警 ===== */
.reconcile-alert {
  margin-top: 14px;
  border-radius: 12px;
}

.missing-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.missing-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 8px;
  font-size: 13px;
}

.missing-name {
  font-weight: 600;
  color: #c45656;
}

.missing-en {
  font-family: "Courier New", monospace;
  color: #909399;
  font-size: 12px;
}

.tab-badge {
  margin-left: 6px;
  transform: translateY(-2px);
}

/* ===== Main ===== */
.main-content {
  padding: 24px 48px 48px;
}

.main-tabs {
  background: #fff;
  border-radius: 14px;
  padding: 22px 26px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
}

.main-tabs :deep(.el-tabs__nav-wrap::after) {
  background: #e2e8f0;
}

.main-tabs :deep(.el-tabs__item) {
  font-size: 15px;
  font-weight: 500;
  height: 44px;
  line-height: 44px;
}

.main-tabs :deep(.el-tabs__item.is-active) {
  color: #667eea;
}

.main-tabs :deep(.el-tabs__active-bar) {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  height: 3px;
  border-radius: 3px;
}

/* ===== File grid ===== */
.file-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 18px;
  padding-top: 14px;
}

.file-card {
  background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  gap: 16px;
  transition: all 0.25s ease;
  cursor: default;
  position: relative;
  overflow: hidden;
}

.file-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.file-card:hover {
  border-color: #c7d2fe;
  box-shadow: 0 12px 28px rgba(102, 126, 234, 0.14);
  transform: translateY(-3px);
}

.file-card:hover::before {
  transform: scaleX(1);
}

.file-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.32);
}

.file-card-content {
  flex: 1;
  min-width: 0;
}

.file-name-cn {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-name-en {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #64748b;
  font-family: "Courier New", monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 10px;
}

.file-name-icon {
  flex-shrink: 0;
  font-size: 14px;
  color: #667eea;
}

.file-tag {
  font-size: 11px !important;
  border-radius: 10px !important;
}

.file-card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dialog-file-name {
  font-family: "Courier New", monospace;
  color: #64748b;
  font-size: 13px;
}

/* ===== Translation ===== */
.trans-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding-top: 10px;
  gap: 12px;
}

.search-input {
  width: 320px;
  border-radius: 10px !important;
}

.refresh-btn {
  border-radius: 10px !important;
}

.cell-cn {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  color: #1e293b;
}

.cell-cn .el-icon {
  color: #11998e;
  font-size: 15px;
}

.cell-en {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: "Courier New", monospace;
  color: #475569;
}

.cell-en .el-icon {
  color: #667eea;
  font-size: 15px;
}

.translation-table :deep(.el-table__row) {
  transition: background 0.15s ease;
}

.translation-table :deep(.el-table__row:hover) {
  background: #f8fafc !important;
}

/* ===== Empty ===== */
.empty-state {
  padding: 40px 0;
}
</style>
